import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, LabelList } from 'recharts';

import { ReactComponent as ArrowCircleRight } from '../../../assets/icons/arrow-circle-right.svg';
import { ReactComponent as CheckBoxActive } from '../../../assets/icons/check-box-active-icon.svg';
import { ReactComponent as CheckBoxInActive } from '../../../assets/icons/check-box-icon.svg';
import { ReactComponent as RoundQuestionBlackIcon } from '../../../assets/icons/round-question-black.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { AddVlbcInterface, DeviceListInterface } from '../../../interface/vlbc.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  fetchChargingStations,
  fetchDevices,
  fetchVlbcs,
  updateDevices,
} from '../../../redux/slices/VlbcSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import vlbcApi from '../../../utils/api/vlbc';

interface AddVlbcProperties {
  handleCancel: () => void;
  closeVlbcModal: () => void;
  fetchChargersAssignCount: () => void;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Add vlbc component
 * @param {any} properties - The properties
 */
function AddVlbc(properties: AddVlbcProperties) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('vlbc');
  const { handleCancel, closeVlbcModal, fetchChargersAssignCount } = properties;
  const { devices, chargingStations } = useAppSelector((states) => states.vlbc);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  const algorithms = [
    {
      label: t('equalSharing'),
      value: 'Equal Sharing',
    },
  ];

  const loadBalancerType = [
    {
      label: t('dynamicLoadBalancing'),
      value: 'Dynamic Load Balancing',
    },
    {
      label: t('staticLoadBalancing'),
      value: 'Static Load Balancing',
    },
  ];

  const [vlbcForm, setVlbcForm] = useState<AddVlbcInterface>({
    loadBalancerName: '',
    maxPowerInAmps: '',
    algorithm: algorithms[0].value,
    chargingStationId: '',
    loadBalancerType: loadBalancerType[0].value,
  });

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  const [selectedDevices, setSelectedDevices] = useState<DeviceListInterface[]>([]);

  /**
   * Function to handle the checkbox click event
   * @param {any} index - The index
   */
  const toggleDeviceLeft = (index: number) => {
    const updatedDevices = devices.data.map((device, index_) => {
      if (index_ === index) {
        return { ...device, isSelected: !device.isSelected };
      }
      return device;
    });

    dispatch(updateDevices(updatedDevices));
  };

  /**
   * Function to handle the checkbox click event
   * @param {any} index - The index
   */
  const toggleDeviceRight = (index: number) => {
    const selectedDevicesTemporary = selectedDevices.map((device, index_) => {
      if (index_ === index) {
        return { ...device, isSelected: !device.isSelected };
      }
      return device;
    });

    setSelectedDevices(selectedDevicesTemporary);
  };

  /**
   * Renders the custom data for the serial number column
   * @param {any} deviceList - The deviceList
   * @param {any} index - The index
   */
  const renderSerialNumber = (deviceList: DeviceListInterface, index: number) => (
    <div className="table-row-each-data select-row">
      <div className="check-box" onClick={() => toggleDeviceLeft(index)} role="presentation">
        <div className="check-box-inner">
          {deviceList.isSelected ? <CheckBoxActive /> : <CheckBoxInActive />}
        </div>
      </div>
      {index + 1}
    </div>
  );

  /**
   * Renders the custom data for the serial number column
   * @param {any} deviceList - The deviceList
   * @param {any} index - The index
   */
  const renderSerialNumberSelectedTable = (deviceList: DeviceListInterface, index: number) => (
    <div className="table-row-each-data select-row">
      <div className="check-box" onClick={() => toggleDeviceRight(index)} role="presentation">
        <div className="check-box-inner">
          {deviceList.isSelected ? <CheckBoxActive /> : <CheckBoxInActive />}
        </div>
      </div>
      {index + 1}
    </div>
  );

  const column = [
    {
      label: t('SNo'),
      accessorKey: 'sno' as keyof DeviceListInterface,
      customData: renderSerialNumber,
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof DeviceListInterface,
      sortAllow: false,
    },

    {
      label: t('noOfConnectors'),
      accessorKey: 'noOfConnectors' as keyof DeviceListInterface,
      sortAllow: false,
    },
    {
      label: t('totalPower'),
      accessorKey: 'maximumOutputPower' as keyof DeviceListInterface,
      sortAllow: false,
    },
  ];

  const selectedTableColumn = [
    {
      label: t('SNo'),
      accessorKey: 'sno' as keyof DeviceListInterface,
      customData: renderSerialNumberSelectedTable,
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof DeviceListInterface,
      sortAllow: false,
    },

    {
      label: t('noOfConnectors'),
      accessorKey: 'noOfConnectors' as keyof DeviceListInterface,
      sortAllow: false,
    },
    {
      label: t('totalPower'),
      accessorKey: 'maximumOutputPower' as keyof DeviceListInterface,
      sortAllow: false,
    },
  ];

  const totalPower = selectedDevices.reduce(
    (sum, device) => sum + Number(device.maximumOutputPower || 0),
    0,
  );

  const percentage = (totalPower / (Number(vlbcForm.maxPowerInAmps) || 1)) * 100;

  const data = [
    {
      name: t('power'),
      value: percentage,
      label: `${totalPower} Amps`,
    },
  ];

  /**
   * Handle the input change function
   */
  const getBarColor = () => {
    if (percentage > 80) return '#ff4d4f';
    if (percentage > 50) return '#faad14';
    return '#52c41a';
  };

  /**
   * Handle the input change function
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setVlbcForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle the move selected devices function
   */
  const moveForward = () => {
    const selectedDevicesTemporary = devices.data.filter((device) => device.isSelected);

    if (!selectedDevicesTemporary.length) return;
    setSelectedDevices([...selectedDevicesTemporary, ...selectedDevices]);

    const unSelectedDevicesTemporary = devices.data.filter((device) => !device.isSelected);
    dispatch(updateDevices(unSelectedDevicesTemporary));
  };

  /**
   * Handle the move selected devices back to main device list
   */
  const moveBackward = () => {
    const selectedDevicesTemporary = selectedDevices.filter((device) => device.isSelected);

    if (!selectedDevicesTemporary.length) return;

    // Add selected devices back to the global list
    const updatedDeviceList = [...selectedDevicesTemporary, ...devices.data];

    dispatch(updateDevices(updatedDeviceList));

    const unSelectedDevicesTemporary = selectedDevices.filter((device) => !device.isSelected);

    setSelectedDevices(unSelectedDevicesTemporary);
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setVlbcForm({
      loadBalancerName: '',
      maxPowerInAmps: '',
      algorithm: algorithms[0].value,
      chargingStationId: '',
      loadBalancerType: loadBalancerType[0].value,
    });

    setSelectedDevices([]);
    setModalBoxStatus('close');
    closeVlbcModal();
  };

  /**
   * Submit Vlbc
   */
  const submit = () => {
    const chargingPointSns = selectedDevices
      .filter((device) => device.isSelected)
      .map((device) => device.chargingPointSn);

    if (
      vlbcForm.loadBalancerName &&
      vlbcForm.maxPowerInAmps &&
      vlbcForm.loadBalancerType &&
      vlbcForm.algorithm &&
      vlbcForm.chargingStationId
    ) {
      setFormStatus({
        showFormErrors: false,
        buttonLoader: true,
      });

      const request: any = {
        loadBalancerName: vlbcForm.loadBalancerName,
        maximumOutputPower: vlbcForm.maxPowerInAmps,
        loadBalancerType: vlbcForm.loadBalancerType,
        algorithm: vlbcForm.algorithm,
        chargingStation: vlbcForm.chargingStationId,
        chargingPoints: chargingPointSns,
      };

      vlbcApi
        .createVlbc(request)
        .then(() => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });

          setModalBoxStatus('open');
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('loadBalancerCreatedSuccessfully')}
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigateBack}
              />
            ),
            notificationModal: true,
          });
          fetchChargersAssignCount();
          dispatch(
            fetchVlbcs({
              page: DefaultApiLimit.page,
              limit: DefaultApiLimit.limit,
            }),
          );
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: true,
            buttonLoader: false,
          });
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message || 'Something went wrong!',
              status: error?.response?.status || 500,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    } else {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: false,
      });
    }
  };

  useEffect(() => {
    if (!devices.loading && vlbcForm.chargingStationId) {
      dispatch(fetchDevices(vlbcForm.chargingStationId));
    }
  }, [vlbcForm.chargingStationId]);

  useEffect(() => {
    dispatch(fetchChargingStations());
  }, []);

  return (
    <div className="add-vlbc-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
        rightCorner=""
        modalBoxAnimation="modalAnimateToLeft"
      />
      <div className="add-vlbc-container">
        <div className="top-form">
          <div>
            {' '}
            <CustomInput
              name="loadBalancerName"
              inputLabel={t('loadBalancerName')}
              type="text"
              placeholder={t('enterTheLoadBalancerName')}
              value={vlbcForm.loadBalancerName}
              onChangeValue={handleInputChange}
              valueRequired
              error={!vlbcForm.loadBalancerName && formStatus.showFormErrors}
              errorMessage={t('loadBalancerIsRequired')}
            />
          </div>
          <div>
            <CustomInput
              name="maxPowerInAmps"
              inputLabel={t('maxPowerAmps')}
              type="number"
              placeholder={t('enterTheMaxPowerInAmps')}
              value={vlbcForm.maxPowerInAmps}
              onChangeValue={handleInputChange}
              valueRequired
              error={!vlbcForm.maxPowerInAmps && formStatus.showFormErrors}
              errorMessage={t('maxPowerAmpsIsRequired')}
            />
          </div>
          <div>
            {' '}
            <SelectField
              inputLabel={t('loadBalancerType')}
              name="loadBalancerType"
              placeholder={t('selectLoadBalancerType')}
              selectedValue={vlbcForm.loadBalancerType}
              onChangeValue={handleInputChange}
              options={loadBalancerType}
              valueRequired
              error={!vlbcForm.loadBalancerType && formStatus.showFormErrors}
              errorMessage={t('loadBalancerTypeIsRequired')}
            />
          </div>
          <div className="algorithm-icon-set">
            <SelectField
              inputLabel={t('algorithm')}
              name="algorithm"
              placeholder={t('selectAlgorithm')}
              selectedValue={vlbcForm.algorithm}
              onChangeValue={handleInputChange}
              options={algorithms}
              valueRequired
              error={!vlbcForm.algorithm && formStatus.showFormErrors}
              errorMessage={t('algorithmIsRequired')}
            />
            <div className="tooltip-hover-wrapper">
              <span className="load-balancer-container-hover">
                <RoundQuestionBlackIcon />
              </span>

              <div className="load-balancer-container">
                <div className="section">
                  <h4 className="subtitle">{t('algorithm')}</h4>

                  <div className="algo-block">
                    <h4>{t('equalShare')}</h4>
                    <p>{t('equalShareDescription')}</p>
                  </div>

                  <div className="algo-block">
                    <h4>{t('firstComeFirstServe')}</h4>
                    <p>{t('firstComeFirstServeDescription')}</p>
                  </div>

                  <div className="algo-block">
                    <h4>{t('roundRobin')}</h4>
                    <p>{t('roundRobinDescription')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="device-selection-wrap">
          <div className="first-section">
            <SelectField
              inputLabel=""
              name="chargingStationId"
              placeholder={t('selectStation')}
              selectedValue={vlbcForm.chargingStationId}
              onChangeValue={handleInputChange}
              options={chargingStations.data}
              valueRequired
            />
            <div className="table-container-page table-container-page-1">
              <div className="table-count">
                {t('deviceList')} ({devices.data.length}){' '}
              </div>

              <CustomTable
                sizeChangeButtonRequired={false}
                loading={devices.loading}
                header={column}
                data={devices.data}
                setHeightAutoToTr
                filterAllow={false}
              />
            </div>
          </div>
          <div className="mover-wrap">
            <div className="mover">
              <span onClick={moveForward} role="presentation">
                <ArrowCircleRight />
              </span>
              <span>
                <ArrowCircleRight onClick={moveBackward} className="arrow-left" />
              </span>
            </div>
          </div>
          <div className="second-section">
            <div className="power-stats">
              {/* Total Power */}
              <span className="label">{t('totalPower')}</span>
              <div className="card total-power">
                <span className="value">{vlbcForm.maxPowerInAmps || 0} Amps</span>
              </div>

              {/* Divider */}
              <div className="divider" />

              {/* Allocated */}
              <span className="label">{t('allocated')}</span>
              <div style={{ width: '150px' }}>
                <ResponsiveContainer width="100%" height={60}>
                  <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <XAxis type="number" domain={[0, 100]} hide />
                    <YAxis dataKey="name" type="category" hide />
                    <Bar
                      dataKey="value"
                      barSize={46}
                      radius={[6, 6, 6, 6]}
                      background={{ fill: '#f0f0f0', radius: 6 }}>
                      <Cell key="power-cell" fill={getBarColor()} />
                      <LabelList
                        dataKey="label"
                        content={({ value }) => (
                          <text
                            x={77}
                            y={30}
                            fill="#007373"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={20}>
                            {value}
                          </text>
                        )}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="table-container-page table-container-page-2">
              <div className="table-count">
                {t('selectedDeviceList')} ({selectedDevices.length}){' '}
              </div>
              <CustomTable
                sizeChangeButtonRequired={false}
                loading={false}
                header={selectedTableColumn}
                data={selectedDevices}
                setHeightAutoToTr
                filterAllow={false}
              />
            </div>
          </div>
        </div>
        <div className="add-vlbc-form-footer">
          <div className="back-button-container">
            <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleCancel} />
          </div>
          <div className="submit-button-container">
            <MyButton
              label={t('submit')}
              buttonType="submit"
              onClickFunc={submit}
              disableButton={formStatus.buttonLoader}
              loading={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddVlbc;
